// Import Dependencies
import PropTypes from "prop-types";
import { useRef, forwardRef, useState } from "react";

// Local Imports
import { useMergedRef } from "hooks";

// ----------------------------------------------------------------------

const Upload = forwardRef((props, ref) => {
  const {
    onChange = () => {},
    children,
    multiple,
    accept,
    name,
    form,
    disabled,
    capture,
    inputProps,
    ...rest
  } = props;
  const inputRef = useRef();
  const [isDragActive, setIsDragActive] = useState(false);

  const onClick = () => {
    !disabled && inputRef.current.click();
  };

  const handleChange = (event) => {
    if (multiple) {
      onChange(Array.from(event.currentTarget.files));
    } else {
      onChange(event.currentTarget.files[0] || null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (multiple) {
        onChange(Array.from(files));
      } else {
        onChange(files[0] || null);
      }
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className="w-full"
    >
      {children({ onClick, disabled, isDragActive, ...rest })}

      <input
        hidden
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        ref={useMergedRef(ref, inputRef)}
        name={name}
        form={form}
        capture={capture}
        {...inputProps}
      />
    </div>
  );
});

Upload.displayName = "Upload";

Upload.propTypes = {
  onChange: PropTypes.func,
  children: PropTypes.func.isRequired,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  capture: PropTypes.string,
  name: PropTypes.string,
  form: PropTypes.string,
  inputProps: PropTypes.object,
};

export { Upload };
